import type { ThemeConfig } from './types';

/**
 * Sensible defaults — match the brand's electric-indigo accent on a neutral
 * white background. Customers can override every value via the editor.
 */
export const DEFAULT_THEME: ThemeConfig = {
  primaryColor: '4f46e5',
  backgroundColor: 'ffffff',
  textColor: '0f172a',
  fontFamily: 'sans',
  radius: 'soft',
};

/** Coerce loosely-typed input into a valid ThemeConfig. Fills missing fields. */
export function normalizeTheme(input: Partial<ThemeConfig> | null | undefined): ThemeConfig {
  if (!input || typeof input !== 'object') return { ...DEFAULT_THEME };
  const bg = typeof input.backgroundImage === 'string' && input.backgroundImage.trim()
    ? input.backgroundImage.trim().slice(0, 2000)
    : undefined;
  return {
    primaryColor: typeof input.primaryColor === 'string' ? input.primaryColor.replace(/^#/, '') : DEFAULT_THEME.primaryColor,
    backgroundColor: typeof input.backgroundColor === 'string' ? input.backgroundColor.replace(/^#/, '') : DEFAULT_THEME.backgroundColor,
    textColor: typeof input.textColor === 'string' ? input.textColor.replace(/^#/, '') : DEFAULT_THEME.textColor,
    fontFamily: ['sans', 'sf-pro', 'serif', 'mono'].includes(input.fontFamily as string)
      ? (input.fontFamily as ThemeConfig['fontFamily'])
      : DEFAULT_THEME.fontFamily,
    radius: ['sharp', 'soft', 'pill'].includes(input.radius as string)
      ? (input.radius as ThemeConfig['radius'])
      : DEFAULT_THEME.radius,
    backgroundImage: bg,
    // Default overlay ON when there's a backgroundImage (better legibility),
    // OFF otherwise. Customer can flip.
    backgroundOverlay: input.backgroundOverlay !== undefined ? !!input.backgroundOverlay : !!bg,
  };
}

/**
 * Produce the CSS variables block. Injected once per page into a <style> tag.
 * Block render functions consume the variables — they never hard-code colors.
 */
export function themeCss(theme: ThemeConfig): string {
  // Explicit SF Pro stack names the Apple display + text variants first so
  // iOS/macOS pick up real San Francisco; everything else falls through to
  // -apple-system (covers older Apple OSes) and finally system-ui /
  // generic sans-serif on Android/Windows.
  const fontStack =
    theme.fontFamily === 'serif'
      ? 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif'
      : theme.fontFamily === 'mono'
        ? 'ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace'
        : theme.fontFamily === 'sf-pro'
          ? '"SF Pro Display", "SF Pro Text", -apple-system, BlinkMacSystemFont, system-ui, "Helvetica Neue", Arial, sans-serif'
          : '-apple-system, BlinkMacSystemFont, "Segoe UI", Inter, Roboto, "Helvetica Neue", Arial, sans-serif';

  const buttonRadius = theme.radius === 'sharp' ? '4px' : theme.radius === 'pill' ? '9999px' : '12px';
  const cardRadius = theme.radius === 'sharp' ? '4px' : theme.radius === 'pill' ? '24px' : '16px';

  return `
  :root {
    --primary: #${theme.primaryColor};
    --bg: #${theme.backgroundColor};
    --text: #${theme.textColor};
    --font: ${fontStack};
    --radius-btn: ${buttonRadius};
    --radius-card: ${cardRadius};
  }`.trim();
}
